import { FunctionComponent } from "react";

import { faLightbulb } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";

import { useAppDispatch } from "@/redux/hooks";

import { useDismissDashboardIssueMutation } from "@/pages/Private/redux/dashboard/dashboard.api";
import { removeIssueLocally } from "@/pages/Private/redux/dashboard/dashboard.slice";

import { DashboardIssue } from "../schema/dashboard";
import { DashboardBox } from "./DashboardBox";
import { DashboardIssueRow } from "./DashboardIssueRow";

interface SuggestionsBoxProps {
	suggestions: DashboardIssue[];
}

/**
 * Advisory items the product cannot decide on its own — today, the competitors waiting to be
 * reviewed (PREC-3594).
 *
 * Deliberately not the red "Issues found" box: nothing here is broken. Dismissing one means "I've
 * reviewed this", and the backend re-arms it when a newer competitor is detected, so the same
 * dismissal that hides an issue reads as a review here.
 */
export const SuggestionsBox: FunctionComponent<SuggestionsBoxProps> = ({ suggestions }) => {
	const dispatch = useAppDispatch();
	const [dismissIssue] = useDismissDashboardIssueMutation();

	if (suggestions.length === 0) {
		return null;
	}

	const handleDismiss = (suggestion: DashboardIssue) => {
		dispatch(removeIssueLocally({ type: suggestion.type, entityId: suggestion.entityId }));
		dismissIssue({ type: suggestion.type, entityId: suggestion.entityId }).catch(() => {
			// optimistic; the backend re-arms on its own conditions
		});
	};

	return (
		<DashboardBox
			accent="teal"
			dismissible={false}
			icon={<Icon className="w-4 h-4" icon={faLightbulb} />}
			subtitle="Worth a look — nothing is broken"
			title="Suggestions"
		>
			{suggestions.map((suggestion) => (
				<DashboardIssueRow
					key={`${suggestion.type}-${suggestion.entityId ?? "none"}`}
					issue={suggestion}
					onActivated={() => handleDismiss(suggestion)}
					onDismiss={() => handleDismiss(suggestion)}
				/>
			))}
		</DashboardBox>
	);
};
